// client/src/components/agendamentos/BloqueioForm.js 

import React, { useState, useEffect, useMemo } from 'react';
import { useMutateBloqueios } from '../../hooks/useBloqueios';
import useAlert from '../../hooks/useAlert';
import { useMe } from '../../hooks/useAuth';
import { useUnidades } from '../../hooks/useUnidades';
import useAgendas from '../../hooks/useAgendas';
import { Save, XCircle, Loader2, Trash2 } from 'lucide-react';
import { fromZonedTime } from 'date-fns-tz';

const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return obj.$oid;
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return obj._id.$oid;
        return obj._id.toString();
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return obj.id.$oid;
        return obj.id.toString();
    }
    return obj.toString();
};

const initialState = {
    entidadeAgendavel: '',
    tipoEntidade: 'User',
    data: new Date().toISOString().split('T')[0],
    horaInicio: '09:00',
    horaFim: '18:00',
    motivo: '',
    diaInteiro: false
};

const BloqueioForm = ({ onSave, onCancel, current, timezone, accountUsers, recursos, unidadeId }) => {
    const { setAlert } = useAlert();
    const { hasPermission, accountId, user: currentUser } = useMe();
    const { data: unidadesData, isLoading: isUnidadesLoading } = useUnidades();
    const { useAgendasQuery } = useAgendas();
    const { data: agendasData, isLoading: isAgendasLoading } = useAgendasQuery({ status: 'ativa', limit: 1000 });
    const { createBloqueio, updateBloqueio, deleteBloqueio } = useMutateBloqueios();
    const isLoading = createBloqueio.isPending || updateBloqueio.isPending || deleteBloqueio.isPending;

    const [bloqueio, setBloqueio] = useState(initialState);
    const [unidadeSelecionada, setUnidadeSelecionada] = useState('');
    
    // ✅ CORREÇÃO: Extrair array de unidades de forma segura
    const unidadesArray = useMemo(() => {
        if (!unidadesData) return [];
        if (Array.isArray(unidadesData)) return unidadesData;
        if (unidadesData.data && Array.isArray(unidadesData.data)) return unidadesData.data;
        if (unidadesData.success && unidadesData.data && Array.isArray(unidadesData.data)) return unidadesData.data;
        if (typeof unidadesData === 'object') {
            const possibleArray = Object.values(unidadesData).filter(v => v && typeof v === 'object' && v._id);
            if (possibleArray.length > 0) return possibleArray;
        }
        return [];
    }, [unidadesData]);
    
    // ✅ CORREÇÃO: Extrair array de agendas de forma segura
    const agendasArray = useMemo(() => {
        if (!agendasData) return [];
        if (Array.isArray(agendasData)) return agendasData;
        if (agendasData.data && Array.isArray(agendasData.data)) return agendasData.data;
        if (agendasData.success && agendasData.data && Array.isArray(agendasData.data)) return agendasData.data;
        return [];
    }, [agendasData]);
    
    // ✅ Função para verificar permissão de agenda
    const verificarPermissaoAgenda = useMemo(() => (agenda) => {
        const roleName = currentUser?.role?.name || currentUser?.roleName || currentUser?.role || '';
        const isAdmin = ['conta_admin', 'matriz_admin', 'franquia_admin'].includes(roleName) || 
                        (currentUser?.permissions || []).includes('conta_admin:full_access') ||
                        currentUser?.isContaAdmin ||
                        hasPermission('conta_admin:full_access') ||
                        hasPermission('matriz_admin:full_access') ||
                        hasPermission('franquia_admin:full_access') ||
                        hasPermission('super_admin:bypass_all_checks');
        
        if (isAdmin) return true;
        
        const currentUserId = extrairIdReal(currentUser?._id || currentUser?.id || currentUser);
        
        if (!agenda || !agenda.permissoes) return false;
        if (extrairIdReal(agenda.entidadeId) === currentUserId) return true;
        
        const p = agenda.permissoes;
        
        if (p.visibilidade === 'publica_na_conta') return true;
        
        if (p.visibilidade === 'publica_na_unidade' && unidadeSelecionada) {
            const unidadesAgenda = (p.unidadesVinculadas || []).map(u => extrairIdReal(u));
            if (unidadesAgenda.length === 0 || unidadesAgenda.includes(extrairIdReal(unidadeSelecionada))) {
                return true;
            }
        }
        
        const visualizadores = (p.visualizadores || []).map(u => extrairIdReal(u));
        const editores = (p.editores || []).map(u => extrairIdReal(u));
        if (visualizadores.includes(currentUserId) || editores.includes(currentUserId)) {
            return true;
        }
        
        return false;
    }, [currentUser, hasPermission, unidadeSelecionada]);
    
    // ✅ Filtrar agendas permitidas para bloqueio
    const agendasPermitidas = useMemo(() => {
        if (!agendasArray.length || !unidadeSelecionada) return { profissionais: [], recursosDisponiveis: [] };
        
        const agendasFiltradas = agendasArray.filter(agenda => {
            if (agenda.tipoEntidade !== 'User' && agenda.tipoEntidade !== 'Profissional' && agenda.tipoEntidade !== 'Recurso') {
                return false;
            }
            return verificarPermissaoAgenda(agenda);
        });
        
        const profissionais = agendasFiltradas
            .filter(a => a.tipoEntidade === 'User' || a.tipoEntidade === 'Profissional')
            .map(agenda => {
                const entidadeId = extrairIdReal(agenda.entidadeId);
                const profissional = accountUsers?.find(u => extrairIdReal(u.user) === entidadeId || extrairIdReal(u._id) === entidadeId);
                return {
                    id: entidadeId,
                    tipo: 'User',
                    nome: agenda.nomeExibicao || profissional?.name || profissional?.user?.name || 'Profissional',
                    agenda: agenda
                };
            });
        
        const recursosDisponiveis = agendasFiltradas
            .filter(a => a.tipoEntidade === 'Recurso')
            .map(agenda => {
                const entidadeId = extrairIdReal(agenda.entidadeId);
                const recurso = recursos?.find(r => extrairIdReal(r) === entidadeId);
                return {
                    id: entidadeId,
                    tipo: 'Recurso',
                    nome: agenda.nomeExibicao || recurso?.nome || 'Recurso',
                    agenda: agenda
                };
            });
        
        return { profissionais, recursosDisponiveis };
    }, [agendasArray, unidadeSelecionada, accountUsers, recursos, verificarPermissaoAgenda]);
    
    useEffect(() => {
        if (current) {
            const formatTime = (isoDate) => isoDate ? new Date(isoDate).toLocaleTimeString('pt-BR', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '';
            const formatDate = (isoDate) => isoDate ? new Date(isoDate).toISOString().split('T')[0] : '';
            
            const entidadeId = current.entidadeAgendavel?._id || current.entidadeAgendavel;
            const tipo = current.tipoEntidade || (current.entidadeAgendavel?.tipo === 'Recurso' ? 'Recurso' : 'User');
            
            setBloqueio({
                ...current,
                entidadeAgendavel: `${entidadeId}-${tipo}`,
                data: formatDate(current.dataInicio),
                horaInicio: current.diaInteiro ? '' : formatTime(current.dataInicio),
                horaFim: current.diaInteiro ? '' : formatTime(current.dataFim),
            });
            
            const unidadeInicial = current.unidade?._id || current.unidade || unidadeId || '';
            setUnidadeSelecionada(unidadeInicial);
        } else {
            setBloqueio(initialState);
            const unidadeInicial = unidadeId || (unidadesArray.length > 0 ? unidadesArray[0]?._id : '');
            setUnidadeSelecionada(unidadeInicial);
        }
    }, [current, unidadesArray, unidadeId]);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        if (name === 'entidadeAgendavel') {
            const [, tipo] = value.split('-');
            setBloqueio(prev => ({ ...prev, entidadeAgendavel: value, tipoEntidade: tipo }));
        } else {
            setBloqueio(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    // ✅ Função unificada para verificar se o usuário pode gerenciar bloqueios
    const podeGerenciarBloqueio = useMemo(() => {
        // Full access (qualquer um deles garante todas as permissões)
        const hasFullAccess = hasPermission('conta_admin:full_access') ||
                              hasPermission('matriz_admin:full_access') ||
                              hasPermission('franquia_admin:full_access') ||
                              hasPermission('super_admin:bypass_all_checks');
        
        if (hasFullAccess) return true;
        
        // Permissões granulares
        return hasPermission('bloqueios:create') ||
               hasPermission('bloqueios:edit') ||
               hasPermission('bloqueios:manage') ||
               hasPermission('bloqueios:delete');
    }, [hasPermission]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Verifica permissão usando a função unificada
        if (current && !podeGerenciarBloqueio) {
            setAlert('Permissão negada para editar bloqueio.', 'danger');
            return;
        }
        if (!current && !podeGerenciarBloqueio) {
            setAlert('Permissão negada para criar bloqueio.', 'danger');
            return;
        }

        if (!bloqueio.entidadeAgendavel || !bloqueio.data || !bloqueio.motivo || !unidadeSelecionada) {
            setAlert('Unidade, Entidade, Data e Motivo são obrigatórios.', 'danger');
            return;
        }
        
        if (!bloqueio.diaInteiro && (!bloqueio.horaInicio || !bloqueio.horaFim)) {
            setAlert('Hora de início e fim são obrigatórias.', 'danger');
            return;
        }
        
        const [entidadeId, tipo] = bloqueio.entidadeAgendavel.split('-');
        if (!entidadeId || !tipo) {
            setAlert('Selecione um profissional ou recurso válido.', 'danger');
            return;
        }
        
        const timeZoneParaUso = timezone || 'America/Sao_Paulo';
        let dataInicioISO, dataFimISO;
        
        if (bloqueio.diaInteiro) {
            dataInicioISO = fromZonedTime(`${bloqueio.data} 00:00:00`, timeZoneParaUso).toISOString();
            dataFimISO = fromZonedTime(`${bloqueio.data} 23:59:59`, timeZoneParaUso).toISOString();
        } else {
            dataInicioISO = fromZonedTime(`${bloqueio.data} ${bloqueio.horaInicio}:00`, timeZoneParaUso).toISOString();
            dataFimISO = fromZonedTime(`${bloqueio.data} ${bloqueio.horaFim}:00`, timeZoneParaUso).toISOString();
        }
        
        const payload = { 
            entidadeAgendavel: entidadeId, 
            tipoEntidade: tipo, 
            dataInicio: dataInicioISO, 
            dataFim: dataFimISO, 
            motivo: bloqueio.motivo, 
            diaInteiro: bloqueio.diaInteiro,
            unidade: unidadeSelecionada,
            account: accountId
        };

        if (current) {
            updateBloqueio.mutate({ id: current._id, bloqueioData: payload }, { 
                onSuccess: () => {
                    setAlert('Bloqueio atualizado com sucesso!', 'success');
                    if (onSave) onSave();
                },
                onError: (error) => {
                    setAlert(error.message || 'Erro ao atualizar bloqueio', 'danger');
                }
            });
        } else {
            createBloqueio.mutate(payload, { 
                onSuccess: () => {
                    setAlert('Bloqueio criado com sucesso!', 'success');
                    if (onSave) onSave();
                },
                onError: (error) => {
                    setAlert(error.message || 'Erro ao criar bloqueio', 'danger');
                }
            });
        }
    };
    
    const handleCancel = () => {
        if (onCancel) onCancel();
    };

    const handleDelete = () => {
        if (!podeGerenciarBloqueio) {
            setAlert('Permissão negada para deletar bloqueio.', 'danger');
            return;
        }
        if (current && window.confirm('Tem certeza que deseja remover este bloqueio?')) {
            deleteBloqueio.mutate(current._id, { 
                onSuccess: () => {
                    setAlert('Bloqueio removido com sucesso!', 'success');
                    if (onSave) onSave();
                },
                onError: (error) => {
                    setAlert(error.message || 'Erro ao remover bloqueio', 'danger');
                }
            });
        }
    };

    const temAgendasDisponiveis = (agendasPermitidas.profissionais?.length > 0) || (agendasPermitidas.recursosDisponiveis?.length > 0);

    return (
        <form onSubmit={handleSubmit} noValidate>
            <fieldset className="form-section">
                <legend className="form-section-title">{current ? 'Editar Bloqueio' : 'Novo Bloqueio'}</legend>
                <div className="form-grid-container">
                    
                    {/* Unidade */}
                    <div className="form-group">
                        <label htmlFor="unidade">Local do Bloqueio*</label>
                        <select 
                            id="unidade" 
                            name="unidade" 
                            value={unidadeSelecionada} 
                            onChange={(e) => setUnidadeSelecionada(e.target.value)} 
                            className="form-input" 
                            required 
                            disabled={isUnidadesLoading}
                        >
                            <option value="">{isUnidadesLoading ? 'Carregando...' : '-- Selecione o Local --'}</option>
                            {unidadesArray.length > 0 ? (
                                unidadesArray.map(unidade => {
                                    const unidadeId = extrairIdReal(unidade);
                                    return (
                                        <option key={unidadeId} value={unidadeId}>
                                            {unidade.nome || unidade.name || 'Unidade sem nome'}
                                        </option>
                                    );
                                })
                            ) : (
                                <option value="" disabled>Nenhuma unidade disponível</option>
                            )}
                        </select>
                        <div className="form-hint">
                            Selecione a unidade para ver as agendas disponíveis
                        </div>
                    </div>

                    {/* Entidade a ser bloqueada - Agendas disponíveis */}
                    <div className="form-group">
                        <label htmlFor="entidadeAgendavel">Bloquear Agenda de*</label>
                        <select 
                            id="entidadeAgendavel" 
                            name="entidadeAgendavel" 
                            value={bloqueio.entidadeAgendavel} 
                            onChange={handleChange} 
                            className="form-input" 
                            required
                            disabled={isAgendasLoading || !unidadeSelecionada}
                        >
                            <option value="">{!unidadeSelecionada ? 'Selecione uma unidade primeiro' : (isAgendasLoading ? 'Carregando agendas...' : '-- Selecione --')}</option>
                            
                            {agendasPermitidas.profissionais?.length > 0 && (
                                <optgroup label="👤 Profissionais">
                                    {agendasPermitidas.profissionais.map(p => {
                                        const key = `prof-${p.id}`;
                                        return (
                                            <option key={key} value={`${p.id}-User`}>
                                                {p.nome}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            )}
                            
                            {agendasPermitidas.recursosDisponiveis?.length > 0 && (
                                <optgroup label="📦 Salas e Recursos">
                                    {agendasPermitidas.recursosDisponiveis.map(r => {
                                        const key = `rec-${r.id}`;
                                        return (
                                            <option key={key} value={`${r.id}-Recurso`}>
                                                {r.nome}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            )}
                        </select>
                        
                        {unidadeSelecionada && !temAgendasDisponiveis && !isAgendasLoading && (
                            <div className="form-hint text-amber-600 mt-1">
                                ⚠️ Nenhuma agenda disponível para bloqueio nesta unidade.
                            </div>
                        )}
                        
                        {!unidadeSelecionada && (
                            <div className="form-hint text-blue-600 mt-1">
                                ℹ️ Selecione uma unidade para ver as agendas disponíveis.
                            </div>
                        )}
                    </div>

                    {/* Data */}
                    <div className="form-group">
                        <label htmlFor="data">Data do Bloqueio*</label>
                        <input 
                            type="date" 
                            id="data" 
                            name="data" 
                            value={bloqueio.data} 
                            onChange={handleChange} 
                            className="form-input" 
                            required 
                        />
                    </div>

                    {/* Dia Inteiro */}
                    <div className="form-group-checkbox self-end mb-2 col-span-full">
                        <input 
                            type="checkbox" 
                            id="diaInteiro" 
                            name="diaInteiro" 
                            checked={bloqueio.diaInteiro} 
                            onChange={handleChange} 
                            className="form-checkbox"
                        />
                        <label htmlFor="diaInteiro">Bloquear o dia inteiro</label>
                    </div>

                    {/* Horários (se não for dia inteiro) */}
                    {!bloqueio.diaInteiro && (
                        <>
                            <div className="form-group">
                                <label htmlFor="horaInicio">Hora de Início*</label>
                                <input 
                                    type="time" 
                                    id="horaInicio" 
                                    name="horaInicio" 
                                    value={bloqueio.horaInicio} 
                                    onChange={handleChange} 
                                    className="form-input" 
                                    required={!bloqueio.diaInteiro} 
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="horaFim">Hora de Fim*</label>
                                <input 
                                    type="time" 
                                    id="horaFim" 
                                    name="horaFim" 
                                    value={bloqueio.horaFim} 
                                    onChange={handleChange} 
                                    className="form-input" 
                                    required={!bloqueio.diaInteiro}
                                />
                            </div>
                        </>
                    )}

                    {/* Motivo */}
                    <div className="form-group col-span-full">
                        <label htmlFor="motivo">Motivo*</label>
                        <input 
                            type="text" 
                            id="motivo" 
                            name="motivo" 
                            value={bloqueio.motivo} 
                            onChange={handleChange} 
                            className="form-input" 
                            required 
                            placeholder="Ex: Feriado, Consulta médica, Manutenção, etc."
                        />
                    </div>
                </div>
            </fieldset>

            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={handleCancel} 
                    className="action-button action-button-secondary"
                    disabled={isLoading}
                >
                    <XCircle className="icon" /> Cancelar
                </button>
                
                {current && podeGerenciarBloqueio && (
                    <button 
                        type="button" 
                        onClick={handleDelete} 
                        className="action-button action-button-destructive" 
                        disabled={isLoading}
                    >
                        <Trash2 className="icon" /> Deletar
                    </button>
                )}
                
                {podeGerenciarBloqueio && (
                    <button 
                        type="submit" 
                        className="action-button action-button-primary" 
                        disabled={isLoading || !temAgendasDisponiveis}
                    >
                        {isLoading ? <Loader2 className="animate-spin icon"/> : <Save className="icon"/>} 
                        {current ? 'Salvar Alterações' : 'Salvar Bloqueio'}
                    </button>
                )}
            </div>
        </form>
    );
};

export default BloqueioForm;